<script lang="ts">
import { getEnv } from '@monogrid/env'
import VersionInfo from '@monogrid/vue-boost/core/VersionInfo.vue'
import { useLocalStorage } from '@vueuse/core'
import type { ComputedRef, InjectionKey } from 'vue'
import { computed, defineComponent, provide, watch } from 'vue'
import { useRoute } from 'vue-router'

import countries from '@/../config/translations/countries'
import { setupHead } from '@/functions/useHead'
import { getSeoImageUrl } from '@/functions/useHead/utils-cms'
import { useQueryVariables } from '@/functions/useQueryVariables'
import { useGetGlobalSeoQuery } from '@/graphql'
import PageLayout from '@/organisms/PageLayout.vue'
import type { Country } from '@/types'
// import { useAudio } from '@/utils/audio'

export const countryInjectionKey: InjectionKey<ComputedRef<Country>> = Symbol('country')

export default defineComponent({
  name: 'App',
  components: {
    PageLayout,
    VersionInfo
  },

  setup () {
    const route = useRoute()
    const env = getEnv()
    // const audio = useAudio('background')
    const query = useGetGlobalSeoQuery({ variables: useQueryVariables() })
    const seoData = computed(() => query.data.value?.seo)
    const countryCode = useLocalStorage('country', (route.query.country as string)?.toLowerCase() || 'default')
    const country = computed(() => ({
      code: countryCode.value,
      ...countries[countryCode.value]
    } as Country))

    watch(() => route.query.country, (value: string) => {
      if (!value) return
      if (value === 'en') {
        countryCode.value = 'default'
        return
      }
      countryCode.value = value.toLowerCase()
    }, { immediate: true })

    setupHead({
      defaultImage: () => getSeoImageUrl(seoData.value?.shareImg),
      defaultOgDescription: () => seoData.value?.ogDescription,
      defaultOgTitle: () => seoData.value?.ogTitle,
      siteDescription: () => seoData.value?.ogDescription,
      siteName: () => seoData.value?.ogTitle || 'Acqua di Parma'
    })

    // onMounted(() => audio.play())

    provide(countryInjectionKey, country)

    return {
      env
    }
  }
})

</script>

<template>
  <div class="app-container relative">
    <PageLayout>
      <RouterView v-slot="{ Component, route }">
        <Transition mode="out-in">
          <component
            :is="Component" v-if="(!route.meta || (route.meta && route.meta.loadingChunk === false)) && Component"
            :key="route.name"
          />
        </Transition>
      </RouterView>
    </PageLayout>
  </div>
</template>

<style lang="scss">
@use "@/styles/main";
</style>

<style lang="scss" scoped>
.app-container {
  min-height: 100vh;
  min-height: 100lvh;
}
</style>
